import { useEffect } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { FormattedMessage, useIntl } from "react-intl";
import { FaArrowLeft, FaArrowRight } from "react-icons/fa6";
import { AuthHeader } from "@/components/AuthHeader";
import { AuthFormCard } from "@/components/AuthFormCard";
import { Button } from "@/components/ui/button";
import { useAuthStore } from "@/lib/stores/authStore";
import { useTrainerAuthRedirect } from "@/lib/hooks/useTrainerAuthRedirect";
import { useLocale } from "@/contexts/IntlContext";
import authImage from "@/assets/exercise-background-photos/register-4.png";

export const Route = createFileRoute("/trainer/auth/")({
  component: TrainerAuthGate,
});

function TrainerAuthGate() {
  const navigate = useNavigate();
  const intl = useIntl();
  const { locale } = useLocale();
  const user = useAuthStore((s) => s.user);
  const { goAfterAuth, setAuthOrigin } = useTrainerAuthRedirect();

  useEffect(() => {
    if (user?.accountType === "TRAINER") {
      goAfterAuth();
    }
  }, [user, goAfterAuth]);

  return (
    <div className="flex min-h-screen flex-col relative">
      <AuthHeader
        image={authImage}
        imageAlt="Train the trainer"
        title={<FormattedMessage id="trainer.auth.title" />}
        subtitle={<FormattedMessage id="trainer.auth.subtitle" />}
        overlay="medium"
      />

      <AuthFormCard>
        <div className="flex flex-col space-y-4">
          <button
            type="button"
            onClick={() => {
              setAuthOrigin(null);
              navigate({ to: "/path" });
            }}
            aria-label={intl.formatMessage({ id: "common.back" })}
            className="absolute items-start text-primary top-5 px-1"
          >
            {locale === "ar" ? (
              <FaArrowRight className="h-4 w-4" />
            ) : (
              <FaArrowLeft className="h-4 w-4" />
            )}
          </button>

          <Link to="/trainer/auth/register">
            <Button className="w-full h-auto py-3 flex flex-col items-center justify-center leading-tight">
              <span>
                <FormattedMessage id="register.createAccount" />
              </span>
              <span className="text-[11px] font-normal opacity-80 mt-0.5">
                <FormattedMessage id="trainer.auth.registerHint" />
              </span>
            </Button>
          </Link>

          <Link to="/trainer/auth/login">
            <Button className="w-full bg-secondary" variant="secondary">
              <FormattedMessage id="register.signIn" />
            </Button>
          </Link>

          <div className="items-center justify-center flex pt-4">
            <button
              className="text-small text-dark"
              onClick={goAfterAuth}
            >
              <FormattedMessage id="trainer.auth.continueAsGuest" />
            </button>
          </div>
        </div>
      </AuthFormCard>
    </div>
  );
}
